import type { RefObject } from "react";

import type { CdropdownMode, CdropdownOption } from "./Cdropdown.types";
import { useDropdownCloseController } from "./useDropdownCloseController";
import { useDropdownInteractionEffects } from "./useDropdownInteractionEffects";
import { useDropdownInteractionHandlers } from "./useDropdownInteractionHandlers";

interface UseDropdownInteractionsInput {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  mode: CdropdownMode;
  options: CdropdownOption[];
  effectiveActiveIndex: number | null;
  isActiveIndexControlled: boolean;
  animateClose: boolean;
  closeOnSelect: boolean;
  dismissOnOutsideClick: boolean;
  keyboardNavigation: boolean;
  triggerDisabled: boolean;
  isSurfaceMeasured: boolean;
  dropdownRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLDivElement | null>;
  setInternalActiveIndex: (index: number | null) => void;
  setHoveredIndex: (index: number | null) => void;
  setActiveIndex: (index: number | null) => void;
}

interface UseDropdownInteractionsResult {
  isAnimating: boolean;
  isClosing: boolean;
  onOptionClick: (option: CdropdownOption) => void;
  onOptionMouseEnter: (input: { index: number; isDisabled: boolean }) => void;
  onOptionMouseLeave: () => void;
  onTriggerClick: () => void;
}

export function useDropdownInteractions(
  input: UseDropdownInteractionsInput,
): UseDropdownInteractionsResult {
  const optionCount = input.options.length;
  const { isAnimating, isClosing, setIsAnimating, handleClose } =
    useDropdownCloseController({
      ...input,
      optionCount,
    });
  const {
    onOptionClick,
    onOptionMouseEnter,
    onOptionMouseLeave,
    onTriggerClick,
  } = useDropdownInteractionHandlers({
    ...input,
    handleClose,
  });

  useDropdownInteractionEffects({
    ...input,
    setIsAnimating,
    handleClose,
    onOptionClick,
  });

  return {
    isAnimating,
    isClosing,
    onOptionClick,
    onOptionMouseEnter,
    onOptionMouseLeave,
    onTriggerClick,
  };
}
